import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Output,
  EventEmitter,
  QueryList,
  ViewChildren,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {Subscription, firstValueFrom, fromEvent} from 'rxjs';
import { ProjectTisComponent } from '../project-tis/project-tis.component';
import {Category, ProjectDetails} from '../../models';
import { Question } from '../../models/definition/question';
import {IQuestionType, QuickscanDefinition} from '../../models';
import { ITisAnswerValue } from '../../models/project/project-data';
import {ProjectAnalysisAnswer} from '../../models/project-analysis';
import { ProjectAnalysisAnswers } from '../../models/project-analysis/project-analysis-answers';
import {ProjectStatus, ProjectSteps} from '../../enums';
import {
    CategoryScrollSpyService,
    IScrollSpyHandle,
    IScrollSpyMarker,
    ProjectAnalysisDefinitionService,
    ToastService,
    UserService
} from '../../services';
import {QuestionAnswerUtil} from "../../utils/question-answer.util";
import {startWith} from "rxjs/operators";
import {TranslateService} from "@ngx-translate/core";

/**
 * PRM 2.0 "Projectanalyse" (formerly step 8 "Aanvullende vragen").
 * Shows the TIS question set of the project one category at a time;
 * the category is selected via the project nav menu (?category=<id>).
 */
@Component({
    selector: 'app-project-analysis-questions',
    templateUrl: './project-analysis-questions.component.html',
    styleUrls: ['./project-analysis-questions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectAnalysisQuestionsComponent extends ProjectTisComponent implements AfterViewInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private projectAnalysisDefinitionService = inject(ProjectAnalysisDefinitionService);
    private userService = inject(UserService);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);
    private scrollSpyService = inject(CategoryScrollSpyService);
    private elementRef = inject(ElementRef<HTMLElement>);

    @Output() projectDetailsChanged = new EventEmitter<ProjectDetails>();

    @ViewChildren('categoryBlock') private categoryBlocks: QueryList<ElementRef<HTMLElement>>;

    public selectedCategoryIndex = 0;
    public categories: Category[] = [];
    public isDefinitiefSaving = false;
    public activeCategoryId = signal<number | null>(null);

    private categorySubscription: Subscription;
    private categoryBlocksSubscription: Subscription;
    private categoryFocusSubscription?: Subscription;
    private scrollSpyHandle?: IScrollSpyHandle;
    private lastScrollSpyWrittenCategoryId: number | null = null;

    /**
     * PRM 2.0 projects run on the project analysis definition.
     */
    protected override async loadDefinitions(): Promise<void> {
        if (this.projectDetails.projectType === null) {
            this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_ANALYSIS_QUESTIONS.no_project_type'));
        } else {
            const definition = await firstValueFrom(
                this.projectAnalysisDefinitionService.getProjectDefinition(this.projectDetails.id)
            );

            this.tisDefinition = QuickscanDefinition.fromAnalysisDefinition(definition, this.isQuickScan());
            this.categories = QuickscanDefinition.convertAnalysisDefinition(definition)?.categories;
        }

        await this.userService.getUserInfo();

        this.restoreDraft();
        this.applyDefaultAnswers();
    }

    protected override isDependencyMet(question: Question): boolean {
        if (question.dependentQuestion == null) {
            return true;
        }

        const dependentQuestion = this.categories.flatMap((category) => category.questions)
            .find(q => q.id === question.dependentQuestion.id);
        if (dependentQuestion == null) {
            return true;
        }

        if (!this.isDependencyMet(dependentQuestion)) {
            return false;
        }

        const dependentValue = this.getAnswerValue(dependentQuestion);

        if (dependentQuestion.type === IQuestionType.BOOL || dependentQuestion.type === IQuestionType.CHECKBOX) {
            return dependentValue.bool === question.dependentQuestion.boolValue;
        } else if (dependentQuestion.type === IQuestionType.MULTIPLECHOICE) {
            return dependentValue.answerId === question.dependentQuestion.answerId;
        } else {
            throw new Error(`Unexpected type ${dependentQuestion.type}`);
        }
    }

    /**
     * A stored draft means the previous session ended (tab reload/close) before its
     * edits made it to the backend, since PageProjectAnalysisStepsComponent normally
     * flushes on every step change; push it now instead of waiting for the next one (PRM-156).
     */
    private restoreDraft(): void {
        const draft = this.quickscanDraftService.load(this.projectDetails.id);
        if (!draft?.length) {
            return;
        }

        this.projectDetails.projectAnalysisAnswers = ProjectAnalysisAnswers.fromDraft(draft as ProjectAnalysisAnswer[]);
        this.pushDraftToBackend().finally();
    }

    /**
     * Unanswered questions with a default value are answered up front, so the
     * default is persisted on the next save instead of only being displayed.
     */
    private applyDefaultAnswers(): void {
        this.tisDefinition.questions
            .filter((question) => question.defaultValue)
            .filter((question) => !this.isQuestionFilled(question))
            .forEach((question) => {
                const value = this.convertDefaultValue(question);
                if (value !== null) {
                    this.setAnswerValue(question, value);
                }
            });
    }

    private convertDefaultValue(question: Question): ITisAnswerValue | null {
        const raw = question.defaultValue;

        switch (question.type) {
            case IQuestionType.TEXT:
                return {text: raw, attachments: []};
            case IQuestionType.INTEGER:
            case IQuestionType.FINANCIAL: {
                const parsed = Number.parseInt(raw, 10);
                return Number.isNaN(parsed) ? null : {integer: parsed, attachments: []};
            }
            case IQuestionType.PERCENTAGE: {
                const parsed = Number.parseFloat(raw);
                return Number.isNaN(parsed) ? null : {percentage: parsed, attachments: []};
            }
            case IQuestionType.BOOL:
            case IQuestionType.CHECKBOX:
                return {bool: raw === 'true', attachments: []};
            case IQuestionType.DATE:
                return {date: raw as unknown as Date, attachments: []};
            default:
                return null;
        }
    }

    public getExplanation(question: Question): string {
        return this.projectDetails.projectAnalysisAnswers.getAnswerForQuestion(question).explanation ?? '';
    }

    public explanationChanged(value: string, question: Question): void {
        this.projectDetails.projectAnalysisAnswers
            .getAnswerForQuestion(question)
            .setRemarks(value);

        this.saveAnalysisDraft();
    }

    public showExplanationField(question: Question): boolean {
        return this.projectDetails.usesProjectAnalysisDefinition() && question.showExplanation === true;
    }

    public get projectVersionLabel(): string | null {
        const version = this.projectDetails?.projectVersion;

        return version ? `v${version.majorVersion}.${version.minorVersion}` : null;
    }

    override async ngOnInit() {
        await super.ngOnInit();

        this.categorySubscription = this.route.queryParamMap.subscribe((params) => {
            this.onCategoryQueryParamChange(Number(params.get('category')));
        });
    }

    private onCategoryQueryParamChange(categoryId: number): void {
        const index = this.categories.findIndex((category) => category.id === categoryId);
        if (index < 0) {
            return;
        }

        this.selectedCategoryIndex = index;

        // Only an externally triggered change (e.g. a nav-menu click) needs to bypass
        // the scroll-spy; a change we wrote ourselves is already in sync with it.
        if (categoryId !== this.lastScrollSpyWrittenCategoryId) {
            this.activeCategoryId.set(categoryId);
            this.scrollSpyHandle?.navigateTo(String(categoryId));
        }
    }

    ngAfterViewInit() {
        this.categoryBlocksSubscription = this.categoryBlocks.changes
            .pipe(startWith(this.categoryBlocks))
            .subscribe(() => this.setupCategoryObserver());

        this.categoryFocusSubscription = fromEvent<FocusEvent>(this.elementRef.nativeElement, 'focusin')
            .subscribe((event) => this.onCategoryBlockFocusIn(event));
    }

    ngOnDestroy() {
        this.categorySubscription?.unsubscribe();
        this.categoryBlocksSubscription?.unsubscribe();
        this.categoryFocusSubscription?.unsubscribe();
        this.scrollSpyHandle?.disconnect();
    }

    /**
     * Scroll-spy: highlights the category the user is currently reading in
     * app-project-nav-menu, instead of only updating on click (RISC menu-follow-focus).
     */
    private setupCategoryObserver(): void {
        this.scrollSpyHandle?.disconnect();

        if (!this.categoryBlocks?.length) {
            return;
        }

        const markers: IScrollSpyMarker[] = this.categoryBlocks.map((block) => ({
            id: block.nativeElement.id.replace('category_', ''),
            element: block.nativeElement
        }));

        this.scrollSpyHandle = this.scrollSpyService.observe(markers, (activeId) => {
            this.activateDominantCategory(Number(activeId));
        });
    }

    /**
     * On short pages (e.g. the Quickscan) every category can be fully in view at
     * once, so scroll coverage never changes as the user tabs between fields.
     * Focusing a field is then the only reliable signal for which category to
     * highlight in app-project-nav-menu.
     */
    private onCategoryBlockFocusIn(event: FocusEvent): void {
        const block = (event.target as HTMLElement | null)?.closest<HTMLElement>('.category-block');
        if (!block) {
            return;
        }

        this.activateDominantCategory(Number(block.id.replace('category_', '')));
    }

    private activateDominantCategory(categoryId: number): void {
        if (categoryId === this.activeCategoryId()) {
            return;
        }

        this.activeCategoryId.set(categoryId);
        this.lastScrollSpyWrittenCategoryId = categoryId;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {category: categoryId},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    public get selectedCategory(): Category | null {
        return this.categories[this.selectedCategoryIndex] ?? null;
    }

    public selectCategory(index: number): void {
        this.selectedCategoryIndex = index;
    }

    public override getUriForStep(stepId: ProjectSteps): string {
        return `/projecten/${this.projectDetails.id}/step/${stepId}`;
    }


    public isQuickScan(): boolean {
        return !this.projectDetails.isQuickscanFinalised();
    };

    public showQuestion(question: Question): boolean {
        if (this.isQuickScan()) {
            return !!question.isQuickScan;
        }
        return true;
    }

    /**
     * A category with no quickscan questions is only hidden during the
     * Quickscan phase; once finalised, all its (TIS) questions show again.
     */
    public categoryHasVisibleQuestions(category: Category): boolean {
        return category.questions.some((question) => this.showQuestion(question));
    }

    public visibleCategories(): Category[] {
        return this.categories.filter((category) => this.categoryHasVisibleQuestions(category));
    }

    public isReadOnly(): boolean {
        return !(this.canEdit());
    }

    public canEdit(): boolean {
        if (this.isQuickScan()) {
            return this.canEditQuickscan();
        }
        return this.canEditTis();
    }

    // XXX Copy from ProjectAnalysisQuickscanComponent
    public definitiefDisabledReason(): string {
        if (this.projectDetails.isOnHold()) {
            return 'DISABLED.project_on_hold';
        }
        if (!this.isQuickscanFilledIn()) {
            return 'PAGE_QUICKSCAN_RESULT.disabled.not_filled_in';
        }
        if (!this.projectService.canEditQuickscan(this.projectDetails)) {
            return 'DISABLED.no_edit_rights';
        }
        if (this.projectDetails.status !== ProjectStatus.INITIAL) {
            return 'DISABLED.quickscan_finalized';
        }
        return '';
    }

    // XXX Copy from ProjectAnalysisQuickscanComponent
    private isQuickscanFilledIn(): boolean {
        if (!this.categories) {
            return false;
        }

        const questions = this.categories
            .flatMap((category) => category.questions);

        const store = this.projectDetails.projectAnalysisAnswers;
        return questions
            .filter((question) => !!question.isQuickScan && question.isRequired !== false)
            .filter((question) => this.isDependencyMet(question))
            .every((question) => {
                const answer = question.isMultiAnswer === true
                    ? store.getAnswerIdsForQuestion(question)
                    : QuestionAnswerUtil.toFormAnswer(question, store.getValueForQuestion(question));

                return QuestionAnswerUtil.isAnswered(question, answer);
            });
    }

    // XXX Copy from ProjectAnalysisQuickscanComponent
    public async makeDefinitive(): Promise<void> {
        if (this.definitiefDisabledReason()) {
            return;
        }

        this.isDefinitiefSaving = true;

        try {
            const saved = await this.projectService.saveProjectDetails(this.projectDetails);
            this.quickscanDraftService.clear(this.projectDetails.id);
            this.emitRefreshedDetails(saved);

            await this.projectService.getQuickscanResult(saved, false, true);

            const refreshed = await this.projectService.getProjectDetails(saved.id);
            this.emitRefreshedDetails(refreshed);

            this.toastService.openSuccess(this.translateService.instant('PAGE_QUICKSCAN_RESULT.message_updated'));
        } catch (error) {
            console.error(error);
            this.toastService.openError(error, this.translateService.instant(
                'ERRORS.quickscan_not_calculated',
                {tisLabelShort: this.projectDetails.projectInfo.tisLabelShort}
            ));
        } finally {
            this.isDefinitiefSaving = false;
        }
    }

    private async pushDraftToBackend(): Promise<void> {
        if (!this.canEditQuickscan() || this.isDefinitiefSaving) {
            return;
        }

        try {
            await this.projectService.saveProjectDetails(this.projectDetails);
            this.quickscanDraftService.clear(this.projectDetails.id);
        } catch (error) {
            console.error(error);
            this.toastService.openError(error, this.translateService.instant('PAGE_PROJECT_ANALYSIS_QUICKSCAN.autosave_error'));
        }
    }

    // XXX Copy from ProjectAnalysisQuickscanComponent
    public canEditQuickscan(): boolean {
        return !this.projectDetails.isMandated()
            && !this.projectDetails.isOnHold()
            && !this.projectDetails.isClosed()
            && this.projectService.canEditQuickscan(this.projectDetails);
    }

    // XXX Copy from ProjectAnalysisQuickscanComponent
    private emitRefreshedDetails(details: ProjectDetails): void {
        this.projectDetails = details;
        this.projectDetailsChanged.emit(details);
    }
}
